/**
 * An invitation somebody opened before they could accept it.
 *
 * Accepting needs the invitee signed in as the invited address, so the usual
 * path leaves the invite page: sign in, or create an account, verify the
 * address in another tab, answer the first-sign-in questions. Any one of those
 * could drop the `redirectUrl` that was meant to bring them back, and then they
 * landed on their own empty dashboard with nothing to say the invitation was
 * still waiting (QA DEV-153: four invitations sent to one person, none
 * accepted, no error anywhere). Remembered here, the first screen after a
 * sign-in returns them to it.
 *
 * `localStorage` rather than the session, because the verification link opens
 * a new tab. The token alone accepts nothing — the server also requires the
 * signed-in address to be the invited one — and it is dropped on acceptance,
 * when the page finds it invalid, and after the invitation's own lifetime.
 */

const KEY = 'pendingInvite'

// The invitation's own lifetime (`INVITATION_TTL_DAYS`, 7). Past it the server
// would refuse the token anyway; this only stops a stale one steering a
// sign-in somewhere useless.
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

type Stored = { token: string; at: number }

export function rememberInvite(token: string) {
    try {
        localStorage.setItem(KEY, JSON.stringify({ token, at: Date.now() }))
    } catch {
        // Storage refused (private window, blocked site data): the invite
        // link still works, it just will not be returned to automatically.
    }
}

export function forgetInvite(token?: string) {
    try {
        if (token && readStored()?.token !== token) {
            return
        }
        localStorage.removeItem(KEY)
    } catch {
        // Nothing to forget.
    }
}

/** Where a sign-in with no other destination should go, if anywhere. */
export function pendingInvitePath(): string | null {
    const stored = readStored()
    if (!stored) {
        return null
    }
    if (Date.now() - stored.at > MAX_AGE_MS) {
        forgetInvite()
        return null
    }
    return `/invite/${encodeURIComponent(stored.token)}`
}

function readStored(): Stored | null {
    try {
        const raw = localStorage.getItem(KEY)
        if (!raw) {
            return null
        }
        const parsed = JSON.parse(raw) as Partial<Stored>
        return typeof parsed.token === 'string' && typeof parsed.at === 'number'
            ? { token: parsed.token, at: parsed.at }
            : null
    } catch {
        return null
    }
}
